import { rtlLanguages, type SrclangType } from "@yedalabs/schema";
import { resolveAvatarTransform } from "@/features/avatars";
import { DEFAULT_BORDER_RADIUS } from "@/features/lomdaEditorV2/components/scene/lib/utils";
import {
    getTemplateAlignment,
    setContentAlignment,
} from "@/features/lomdaEditorV2/lib/sceneContentBlocks";
import { templateHasMediaSlot } from "@/features/lomdaEditorV2/lib/sceneTemplates";
import {
    type LomdaScene,
    type SceneTemplateVariation,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import { CANVAS_H, CANVAS_W, centeredBox } from "./transform";
import {
    createAvatarElement,
    createMediaElement,
    createTextElement,
} from "./factories";
import { CENTERED_TEXT, TEMPLATE_LAYOUTS } from "./layouts/templates";
import { mirrorSlotsForRtl } from "./layouts/apply";
import type { ElementTransform, SceneElement } from "./types";

export type LayoutDirection = "ltr" | "rtl";

/** Reading direction of a lomda from its content language ("he"/"ar" → rtl). */
export function directionOf(
    language: SrclangType | null | undefined
): LayoutDirection {
    return language && rtlLanguages.includes(language) ? "rtl" : "ltr";
}

function avatarTransform(scene: LomdaScene): ElementTransform | null {
    if (!scene.avatar) return null;
    const t = resolveAvatarTransform(scene.avatar);
    if (!t) return null;
    const aspect = t.aspectRatio ?? 1;
    const heightPct = (t.width * CANVAS_W) / (aspect * CANVAS_H);
    return {
        x: t.x,
        y: t.y,
        width: t.width,
        height: heightPct,
        rotation: t.rotation ?? 0,
    };
}

/**
 * Convert a legacy template-based scene into the free-form `elements` model.
 * Positions + text alignment match the original A–E templates so existing
 * lomdas open looking identical. Idempotent.
 *
 * `direction` mirrors asymmetric layouts (e.g. template B) for RTL lomdas, just
 * as the original `flex-row` templates auto-reversed under `dir="rtl"`.
 */
export function migrateSceneToElements(
    scene: LomdaScene,
    direction: LayoutDirection = "ltr"
): SceneElement[] {
    if (scene.elements) return scene.elements;

    const template: SceneTemplateVariation = scene.template ?? "A";
    const tpl = TEMPLATE_LAYOUTS[template] ?? TEMPLATE_LAYOUTS.A;
    const align = getTemplateAlignment(template) ?? tpl.align;
    const slots = direction === "rtl" ? mirrorSlotsForRtl(tpl.slots) : tpl.slots;

    const elements: SceneElement[] = [];

    const html = setContentAlignment(scene.content || "<p></p>", align);
    elements.push(
        createTextElement(
            html,
            slots.text ?? CENTERED_TEXT,
            0,
            tpl.textVerticalAlign
        )
    );

    // Media slot (B/C/D/E). For A/E the legacy `imageUrl` is a background, not
    // a floating element, so only promote `mediaItem` or a slot-template imageUrl.
    const media =
        scene.mediaItem ??
        (scene.imageUrl && templateHasMediaSlot(template)
            ? ({ type: "image", url: scene.imageUrl } as const)
            : null);
    if (media) {
        const fit = scene.layout?.mediaObjectFit ?? tpl.mediaFit;
        const radius =
            template === "D"
                ? 0
                : (scene.layout?.mediaBorderRadius ?? DEFAULT_BORDER_RADIUS);
        elements.push(
            createMediaElement(
                { type: media.type, url: media.url, objectFit: fit, borderRadius: radius },
                slots.media ?? centeredBox(42, 56),
                1
            )
        );
    }

    if (scene.avatar) {
        const t = avatarTransform(scene);
        if (t) elements.push(createAvatarElement(scene.avatar, t, 1000));
    }

    return elements;
}

/** Ensure a scene has an `elements` array, migrating from legacy fields if needed. */
export function ensureSceneElements(
    scene: LomdaScene,
    direction: LayoutDirection = "ltr"
): LomdaScene {
    if (scene.elements) return scene;
    return { ...scene, elements: migrateSceneToElements(scene, direction) };
}
